import {
  deltaKindSchema,
  EdlinkOrgChartDryRunResult,
  EdlinkOrgChartImportSummary,
  labelForEdlinkResult,
} from '@airreading/common/data';
import { DeltaKindGroup } from './delta-kind-group';
import { NormalizedEntityItem } from '@/lib/edlink-utils';
import { EdlinkIdCode } from '@/components/alphanumeric-code';
import { ExpandableSummarySection } from './expandable-summary-section';

interface ImportSummaryProps {
  summary: EdlinkOrgChartImportSummary | EdlinkOrgChartDryRunResult;
  districtName: string;
  districtId: string;
}

const normalizeDryRunDelta = (delta: EdlinkOrgChartDryRunResult['items'][number]): NormalizedEntityItem => {
  const internalId = delta.kind !== 'create' ? delta.id : undefined;
  const externalId = delta.kind === 'create' ? delta.externalId : undefined;
  return {
    type: delta.delta,
    kind: delta.kind,
    internalId,
    externalId,
    label: labelForEdlinkResult(delta.delta, delta.kind, internalId, externalId),
  };
};

const normalizeDryRunOutcome = ({
  delta,
  kind,
  label,
  details,
}: EdlinkOrgChartDryRunResult['failures'][number]): NormalizedEntityItem => ({
  type: delta,
  kind,
  label,
  details,
});

export function ImportSummary({ summary, districtName, districtId }: ImportSummaryProps) {
  const isDryRun = !(summary instanceof EdlinkOrgChartImportSummary);
  const items = isDryRun ? summary.items.map(normalizeDryRunDelta) : summary.items;
  const failures = isDryRun ? summary.failures.map(normalizeDryRunOutcome) : summary.failureItems;
  const skips = isDryRun ? summary.skips.map(normalizeDryRunOutcome) : summary.skipItems;
  const deltaByKind = Map.groupBy(items, (item) => item.kind);

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm mb-4">
        <span className="font-bold">{isDryRun ? 'Dry Run' : 'Import'}:</span> {districtName} -{' '}
        <EdlinkIdCode code={districtId} showCopyButton={false} showColor={false} />
      </div>
      {deltaKindSchema.options.map((kind) => (
        <DeltaKindGroup key={kind} kind={kind} items={deltaByKind.get(kind) ?? []} isDryRun={isDryRun} />
      ))}
      <ExpandableSummarySection
        label={isDryRun ? 'Will fail' : 'Failed items'}
        color="border-l-orange-500"
        count={failures.length}
        items={failures}
      />
      <ExpandableSummarySection
        label={isDryRun ? 'Will be skipped' : 'Skipped items'}
        color="border-l-gray-400"
        count={skips.length}
        items={skips}
      />
    </div>
  );
}
